/**
 * IntelligenceCharts — standard chart suite for Admin Intelligence reports.
 * Same slot/order for every report; only the data inside changes.
 *
 * Each chart sits in a shared `<DownloadableChartCard>` (also reused across
 * other admin pages) which exposes a PNG / JPEG download menu.
 */
import React, { useState } from "react";
import {
  ResponsiveContainer,
  LineChart, Line,
  PieChart, Pie, Cell,
  BarChart, Bar,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid, LabelList, ReferenceLine,
} from "recharts";
import DownloadableChartCard from "./admin/DownloadableChartCard";

const ChartCard = DownloadableChartCard;

// Stable, color-blind-friendly palette (greens + ambers + slates).
const PALETTE = [
  "#16A34A", // mango green (brand)
  "#F59E0B",
  "#0EA5E9",
  "#DC2626",
  "#7C3AED",
  "#0F766E",
  "#F472B6",
  "#475569",
  "#65A30D",
  "#EA580C",
];

// Custom tick renderer for long axis labels — wraps onto 2 lines so the full
// name (e.g. "Islamabad Capital Territory") fits without truncation.
function WrappedTick({ x, y, payload, width = 150 }) {
  const text = String(payload?.value ?? "");
  // Split on whitespace, greedily pack into <= ~18 char lines.
  const max = Math.max(12, Math.floor(width / 7));
  const words = text.split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > max) {
      if (cur) lines.push(cur);
      cur = w;
    } else {
      cur = (cur ? cur + " " : "") + w;
    }
  }
  if (cur) lines.push(cur);
  return (
    <g transform={`translate(${x},${y})`}>
      {lines.slice(0, 2).map((ln, i) => (
        <text key={i} x={-6} y={i * 11 + (lines.length === 1 ? 4 : -2)} textAnchor="end" fontSize={11} fill="#333">
          {ln}
        </text>
      ))}
    </g>
  );
}

// Stable tick-renderer factories (kept outside render to satisfy React's
// component-stability rule — recreating these inside `render` made React
// destroy/remount each axis subtree on every paint).
const tickW130 = (props) => <WrappedTick {...props} width={130} />;
const tickW170 = (props) => <WrappedTick {...props} width={170} />;
const tickW180 = (props) => <WrappedTick {...props} width={180} />;

// ---------- Universal strip ----------
export function UniversalStrip({ universal, scope }) {
  if (!universal) return null;
  const tiles = [
    { label: "Sample size (n)", value: universal.total_n ?? 0 },
    { label: scope === "tracker" ? "Waves analysed" : "Latest wave n", value: scope === "tracker" ? universal.waves_count : universal.latest_n },
    { label: "Gender split", value: Object.entries(universal.gender_split || {}).map(([k, v]) => `${k.slice(0, 1)}: ${v}%`).join("  ·  ") || "—" },
    { label: "Top region", value: (() => {
      const r = universal.region_split || {};
      const top = Object.entries(r).sort((a, b) => b[1] - a[1])[0];
      return top ? `${top[0]} (${top[1]}%)` : "—";
    })() },
  ];
  return (
    <ChartCard title="Sample summary" subtitle="Base for everything below" filenameBase="sample-summary">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <div key={t.label} className="border border-neutral-200 px-3 py-2">
            <div className="overline text-[9px] text-neutral-500">{t.label}</div>
            <div className="font-black text-base mt-0.5 tabular-nums">{t.value}</div>
          </div>
        ))}
      </div>
    </ChartCard>
  );
}

// ---------- Tracker: Trendline ----------
function Trendline({ data, optionLabels }) {
  if (!data || data.length === 0) return <EmptyState>No trend data.</EmptyState>;
  const keys = Object.keys(data[0]).filter((k) => k !== "wave");
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis dataKey="wave" tick={{ fontSize: 11 }} />
        <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v) => `${v}%`} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {keys.map((k, i) => (
          <Line key={k} type="monotone" dataKey={k} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={{ r: 3 }} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

// ---------- Latest snapshot pie ----------
function SnapshotPie({ data }) {
  if (!data || data.length === 0) return <EmptyState>No snapshot data.</EmptyState>;
  const filtered = data.filter((d) => d.pct > 0);
  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie data={filtered} dataKey="pct" nameKey="option" cx="40%" cy="50%" outerRadius={90} label={(d) => `${d.pct}%`}>
          {filtered.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Pie>
        <Tooltip formatter={(v) => `${v}%`} />
        <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ---------- Delta bar (first vs last) ----------
function DeltaBar({ data }) {
  if (!data || data.length === 0) return <EmptyState>No delta data.</EmptyState>;
  return (
    <ResponsiveContainer width="100%" height={Math.max(220, data.length * 44)}>
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 40, bottom: 8, left: 160 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis type="number" tickFormatter={(v) => `${v > 0 ? "+" : ""}${v}`} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="option" tick={tickW170} width={170} interval={0} />
        <Tooltip formatter={(v) => `${v > 0 ? "+" : ""}${v} pp`} />
        <ReferenceLine x={0} stroke="#999" />
        <Bar dataKey="change">
          {data.map((d, i) => <Cell key={i} fill={d.change >= 0 ? "#16A34A" : "#DC2626"} />)}
          <LabelList dataKey="change" position="right" formatter={(v) => `${v > 0 ? "+" : ""}${v}`} style={{ fontSize: 10, fill: "#333" }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ---------- Demographic split (stacked bar across one axis) ----------
function DemographicSplit({ axes }) {
  const [axis, setAxis] = useState("region");
  if (!axes || !axes[axis] || axes[axis].length === 0) {
    return <EmptyState>No demographic data.</EmptyState>;
  }
  const rows = axes[axis];
  const keys = Object.keys(rows[0]).filter((k) => k !== "bucket");
  return (
    <div>
      <div className="flex gap-1 mb-3">
        {["region", "age_band", "gender", "sec"].map((ax) => (
          <button
            key={ax}
            type="button"
            onClick={() => setAxis(ax)}
            className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${axis === ax ? "bg-black text-white border-black" : "border-neutral-200 hover:bg-neutral-100"}`}
            data-testid={`demo-axis-${ax}`}
          >
            {ax === "age_band" ? "Age" : ax === "sec" ? "SEC" : ax}
          </button>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={Math.max(240, rows.length * 48)}>
        <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 180 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis type="number" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} domain={[0, 100]} />
          <YAxis type="category" dataKey="bucket" tick={tickW180} width={180} interval={0} />
          <Tooltip formatter={(v) => `${v}%`} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {keys.map((k, i) => (
            <Bar key={k} dataKey={k} stackId="a" fill={PALETTE[i % PALETTE.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------- Supporting cross-tab (stacked bar) ----------
function SupportingCrosstab({ data }) {
  if (!data || !data.rows || data.rows.length === 0) return <EmptyState>No supporting data.</EmptyState>;
  const keys = Object.keys(data.rows[0]).filter((k) => k !== "tracker_label");
  return (
    <ResponsiveContainer width="100%" height={Math.max(240, data.rows.length * 48)}>
      <BarChart data={data.rows} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 170 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis type="number" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} domain={[0, 100]} />
        <YAxis type="category" dataKey="tracker_label" tick={tickW170} width={170} interval={0} />
        <Tooltip formatter={(v) => `${v}%`} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        {keys.map((k, i) => (
          <Bar key={k} dataKey={k} stackId="a" fill={PALETTE[i % PALETTE.length]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ---------- Wave: Question distribution grid ----------
function QuestionGrid({ items }) {
  if (!items || items.length === 0) return <EmptyState>No question data.</EmptyState>;
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {items.map((q) => (
        <div key={q.qid} className="border border-neutral-100 p-2">
          <div className="text-[11px] font-bold leading-tight mb-1 line-clamp-2 min-h-[28px]">
            {q.is_tracker && <span className="text-mango mr-1">●</span>}
            {q.question}
          </div>
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={q.data} layout="vertical" margin={{ top: 4, right: 36, bottom: 4, left: 130 }}>
              <XAxis type="number" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 9 }} domain={[0, 100]} />
              <YAxis type="category" dataKey="option" tick={tickW130} width={130} interval={0} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="pct" fill="#16A34A">
                <LabelList dataKey="pct" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 9, fill: "#333" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ))}
    </div>
  );
}

// ---------- Wave: Cross-tab heatmap ----------
function CrosstabHeatmap({ data }) {
  if (!data || !data.matrix || data.matrix.length === 0) return <EmptyState>Not enough single-choice questions for a cross-tab.</EmptyState>;
  const max = Math.max(...data.matrix.flat());
  const intensity = (v) => {
    if (max <= 0) return "#fff";
    const t = v / max;
    // light → mango
    const r = Math.round(255 - (255 - 22) * t);
    const g = Math.round(255 - (255 - 163) * t);
    const b = Math.round(255 - (255 - 74) * t);
    return `rgb(${r},${g},${b})`;
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] border-collapse">
        <thead>
          <tr>
            <th className="text-left p-2 font-bold border-b">{data.row_question}</th>
            {data.col_labels.map((c) => (
              <th key={c} className="text-center p-2 font-bold border-b text-[10px]" title={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.matrix.map((row, ri) => (
            <tr key={ri}>
              <td className="p-2 font-medium border-r border-neutral-100">{data.row_labels[ri]}</td>
              {row.map((v, ci) => (
                <td key={ci} className="text-center p-2 tabular-nums" style={{ background: intensity(v), color: v / Math.max(max, 1) > 0.55 ? "#fff" : "#222" }}>
                  {v}%
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[10px] text-neutral-500 mt-2">
        Rows: <em>{data.row_question}</em> · Columns: <em>{data.col_question}</em> · Cells: % of total respondents in that combination.
      </div>
    </div>
  );
}

// ---------- Wave: Sample composition ----------
function SampleComposition({ data }) {
  if (!data) return <EmptyState>No composition data.</EmptyState>;
  const axes = ["region", "age_band", "gender", "sec"];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {axes.map((ax) => {
        const rows = data[ax] || [];
        if (rows.length === 0) return (
          <div key={ax} className="border border-neutral-100 p-2 text-center text-[10px] text-neutral-400 italic">No {ax}</div>
        );
        return (
          <div key={ax} className="border border-neutral-100 p-2">
            <div className="overline text-[9px] mb-1">{ax === "age_band" ? "Age band" : ax === "sec" ? "SEC" : ax}</div>
            <ResponsiveContainer width="100%" height={120}>
              <PieChart>
                <Pie data={rows} dataKey="pct" nameKey="name" outerRadius={50}>
                  {rows.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `${v}%`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="text-[9px] mt-1 space-y-0.5">
              {rows.slice(0, 5).map((r, i) => (
                <div key={r.name} className="flex items-center gap-1">
                  <span className="w-2 h-2 inline-block" style={{ background: PALETTE[i % PALETTE.length] }} />
                  <span className="truncate flex-1">{r.name}</span>
                  <span className="tabular-nums">{r.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Wave: Tracker-question demographic with optional picker ----------
function TrackerDemographicSection({ charts }) {
  // Prefer the new `tracker_demographics` array; fall back to legacy `marquee_demographic`.
  const items = (charts.tracker_demographics && charts.tracker_demographics.length > 0)
    ? charts.tracker_demographics
    : (charts.marquee_demographic ? [{ qid: "legacy", question: charts.marquee_demographic.question, axes: charts.marquee_demographic.axes }] : []);
  const [pickIdx, setPickIdx] = useState(0);
  if (items.length === 0) {
    return <EmptyState>No tracker question in this wave.</EmptyState>;
  }
  const active = items[Math.min(pickIdx, items.length - 1)];
  return (
    <div>
      {items.length > 1 && (
        <div className="mb-3 flex items-center gap-2">
          <span className="overline text-[10px] text-neutral-500">Tracker question</span>
          <select
            value={pickIdx}
            onChange={(e) => setPickIdx(Number(e.target.value))}
            className="text-xs border border-neutral-300 px-2 py-1 bg-white max-w-md truncate"
            data-testid="tracker-question-picker"
          >
            {items.map((it, i) => (<option key={it.qid || i} value={i}>{it.question}</option>))}
          </select>
        </div>
      )}
      <div className="text-[11px] text-neutral-500 italic mb-2 line-clamp-2">{active.question}</div>
      <DemographicSplit axes={active.axes} />
    </div>
  );
}

function EmptyState({ children }) {
  return <div className="text-xs text-neutral-400 italic text-center py-8">{children}</div>;
}

// ---------- Public composer ----------
export default function IntelligenceCharts({ scope, charts }) {
  if (!charts) {
    return (
      <div className="px-5 py-4 text-xs text-neutral-400 italic border-y border-neutral-100">
        Charts not available for this report (legacy report — re-generate to see visuals).
      </div>
    );
  }
  if (scope === "tracker") {
    return (
      <div className="bg-neutral-50 p-4 space-y-4 border-y border-neutral-200" data-testid="intel-charts-tracker">
        <UniversalStrip universal={charts.universal} scope="tracker" />
        <ChartCard title="1. Trendline — tracker question across waves" subtitle="% share per option, wave-over-wave" filenameBase="01-trendline">
          <Trendline data={charts.trendline} optionLabels={charts.option_labels} />
        </ChartCard>
        <ChartCard title="2. Latest snapshot" subtitle="Distribution in the most recent wave" filenameBase="02-latest-snapshot">
          <SnapshotPie data={charts.latest_snapshot} />
        </ChartCard>
        <ChartCard title="3. Wave-over-wave delta" subtitle="Percentage-point change from first → latest wave" filenameBase="03-delta">
          <DeltaBar data={charts.delta} />
        </ChartCard>
        <ChartCard title="4. Demographic split (latest wave)" subtitle="Tracker answers broken down by region / age / gender / SEC" filenameBase="04-demographic-split">
          <DemographicSplit axes={charts.demographic_split} />
        </ChartCard>
        <ChartCard
          title="5. Supporting cross-tab (latest wave)"
          subtitle={charts.supporting_crosstab ? `Tracker answer × ${charts.supporting_crosstab.supporting_question}` : "—"}
          filenameBase="05-supporting-crosstab"
        >
          <SupportingCrosstab data={charts.supporting_crosstab} />
        </ChartCard>
      </div>
    );
  }
  // wave scope
  return (
    <div className="bg-neutral-50 p-4 space-y-4 border-y border-neutral-200" data-testid="intel-charts-wave">
      <UniversalStrip universal={charts.universal} scope="wave" />
      <ChartCard title="1. Question distribution grid" subtitle="One distribution per question in the wave" filenameBase="01-question-grid">
        <QuestionGrid items={charts.question_grid} />
      </ChartCard>
      <ChartCard
        title="2. Demographic Breakdown — Tracker Question"
        subtitle={(() => {
          const items = (charts.tracker_demographics && charts.tracker_demographics.length > 0)
            ? charts.tracker_demographics
            : (charts.marquee_demographic ? [charts.marquee_demographic] : []);
          if (items.length === 0) return "No tracker question";
          if (items.length === 1) return items[0].question;
          return `${items.length} tracker questions — pick from dropdown`;
        })()}
        filenameBase="02-tracker-demographic"
      >
        <TrackerDemographicSection charts={charts} />
      </ChartCard>
      <ChartCard title="3. Cross-tab heatmap" subtitle="Two strongest single-choice questions, joint distribution" filenameBase="03-crosstab-heatmap">
        <CrosstabHeatmap data={charts.crosstab_heatmap} />
      </ChartCard>
      <ChartCard title="4. Sample composition" subtitle="Who answered — region / age / gender / SEC" filenameBase="04-sample-composition">
        <SampleComposition data={charts.sample_composition} />
      </ChartCard>
    </div>
  );
}
